import { useEffect, useMemo, useState } from 'react'
import { SCHEDULE_DEFAULT_TZ, isValidTimeZone } from '@papan/types'
import { formatZonedTime, nextCronRuns } from './cron-next-runs.js'

/** The viewer's own time zone (the browser's), or null if it can't be read. */
export function browserTimeZone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    return isValidTimeZone(tz) ? tz : null
  } catch { return null }
}

/**
 * The next fire times of a cron schedule — the explanation of a pattern, instead of a
 * paraphrase: each run in the schedule's own zone and, when the viewer is elsewhere, on the
 * viewer's clock ("Wed 30 Sep, 09:00 UTC → Wed 30 Sep, 17:00 your time"). The same engine
 * engine BullMQ fires by (`nextCronRuns`). `[]` for anything it cannot read — a hand-edited
 * `triggers.json` can hold any value; the caller already shows the raw pattern.
 */
export function nextRunLines(cron: unknown, tz: unknown, count: number, now: number = Date.now()): string[] {
  // Only an ABSENT zone means UTC (as describeCronSchedule): `""` or junk is unreadable → no runs.
  const zone = tz == null ? SCHEDULE_DEFAULT_TZ : tz
  let runs: number[] | null = null
  try { runs = nextCronRuns(cron, zone, count, now) } catch { runs = null }
  if (!runs) return []
  const zoneName = zone as string // nextCronRuns returned runs ⇒ it validated the zone
  const mine = browserTimeZone()
  const other = mine && mine !== zoneName
  return runs.map((t) => {
    const there = `${formatZonedTime(t, zoneName, now)} ${zoneName}`
    if (!other) return there
    const here = formatZonedTime(t, mine as string, now)
    return here === formatZonedTime(t, zoneName, now) ? there : `${there} → ${here} your time`
  })
}

/** Small muted "Next: …" line(s) for a cron schedule; renders nothing for an interval or
 *  an unreadable schedule. */
export function ScheduleNextRuns({ cron, tz, count = 1, className = '', prefix }: { cron?: unknown; tz?: unknown; count?: number; className?: string; prefix?: string }) {
  // Memoised (the editor re-renders on every keystroke in unrelated fields), keyed on the
  // current MINUTE — and a once-a-minute tick re-renders it, so a panel left open never keeps
  // showing a run that has passed.
  const [minute, setMinute] = useState(() => Math.floor(Date.now() / 60_000))
  useEffect(() => {
    const t = setInterval(() => setMinute(Math.floor(Date.now() / 60_000)), 15_000)
    return () => clearInterval(t)
  }, [])
  const lines = useMemo(
    () => (typeof cron === 'string' && cron.trim() ? nextRunLines(cron, tz, count, minute * 60_000) : []),
    [cron, tz, count, minute],
  )
  if (lines.length === 0) return null
  return (
    <div className={`text-[11px] themed-text-muted ${className}`}>
      {count === 1
        ? <>{prefix ?? 'Next run'}: {lines[0]}</>
        : <>Next runs:<ul className="mt-0.5 space-y-0.5">{lines.map((l) => <li key={l}>{l}</li>)}</ul></>}
    </div>
  )
}
